import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BUSINESSES, LEARNING_TIPS, ACHIEVEMENTS } from "@/data/gameData";
import { useGame } from "@/context/GameContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import HUD from "@/components/HUD";
import LearningTip from "@/components/LearningTip";
import PiggyBankModal from "@/components/PiggyBankModal";
import Shop from "@/components/Shop";
import EndOfDay from "@/components/EndOfDay";
import { Sparkles, PiggyBank, ShoppingBag, Moon, Trophy, RotateCcw, BookOpen, Palette, Coins, Lock, Megaphone, KeyRound, Save, Info } from "lucide-react";
import { adminUnlock } from "@/lib/payments";
import { toast } from "sonner";
import { Sound } from "@/lib/sound";
import Avatar from "@/components/avatar/Avatar";
import Mascot from "@/components/avatar/Mascot";
import AvatarEditor from "@/components/avatar/AvatarEditor";
import RivalCard from "@/components/rival/RivalCard";
import WeekResultModal from "@/components/rival/WeekResultModal";
import BrandBadge from "@/components/brand/BrandBadge";
import SaveLoadModal from "@/components/SaveLoadModal";
import LeaderboardCard from "@/components/LeaderboardCard";
import AdminPanel from "@/components/admin/AdminPanel";

const Hub = () => {
  const { state, setPlayerName, setAvatar, setAvatarMakerOpen, resetGame, resetMoney, isBusinessLocked, setPaywallOpen, markAutoTipDay, getBrand, setUnlocked } = useGame();
  const navigate = useNavigate();
  const [adminCode, setAdminCode] = useState(null);
  const [tempName, setTempName] = useState("");
  const [onboardStep, setOnboardStep] = useState("name");
  const [tip, setTip] = useState(null);
  const [piggyOpen, setPiggyOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [dayEndOpen, setDayEndOpen] = useState(false);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [flyerPickerOpen, setFlyerPickerOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  // Auto tip: at most once per day, and only every other day (kids can open lessons any time)
  useEffect(() => {
    if (!state.playerName) return;
    if (state.lastAutoTipDay === state.day || state.day % 2 === 0) return;
    const unseen = LEARNING_TIPS.filter((t) => !state.seenTips.includes(t.id));
    if (unseen.length === 0) return;
    const t = setTimeout(() => { setTip(unseen[Math.floor(Math.random() * unseen.length)]); markAutoTipDay(); }, 2500);
    return () => clearTimeout(t);
  }, [state.playerName, state.day]); // eslint-disable-line

  if (!state.playerName) {
    if (onboardStep === "avatar") {
      return (
        <div className="min-h-screen flex items-center justify-center px-4 py-8 dotted-bg" data-testid="onboarding-avatar-screen">
          <div className="max-w-3xl w-full bg-white rounded-3xl border-4 border-amber-300 p-6 sm:p-8 card-3d">
            <div className="mb-5">
              <div className="text-xs font-black uppercase tracking-widest text-amber-600 mb-1">Step 2 of 2</div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                Make it yours, {tempName.trim()}!
              </h1>
              <p className="mt-2 text-slate-600 font-medium">Pick your look and a mascot pet to cheer you on.</p>
            </div>
            <AvatarEditor
              initial={state.avatar}
              saveLabel="Open my business! →"
              onSave={(a) => {
                Sound.primeOnFirstInteraction();
                Sound.fanfare();
                setAvatar(a);
                setPlayerName(tempName.trim());
              }}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center px-4 dotted-bg" data-testid="onboarding-screen">
        <div className="max-w-lg w-full bg-white rounded-3xl border-4 border-amber-300 p-8 card-3d">
          <div className="text-center mb-6">
            <div className="text-7xl mb-3 animate-bounce-slow">💼</div>
            <div className="text-xs sm:text-sm font-black uppercase tracking-[0.3em] text-slate-500 mb-1">Welcome to</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-amber-500 tracking-tight text-center whitespace-nowrap" data-testid="welcome-title">
              Kids Biz Boss!
            </h1>
            <p className="mt-3 text-slate-600 font-medium">
              Pick a business, take real jobs, and learn how money works — all through play!
            </p>
          </div>
          <div className="space-y-3">
            <label className="block text-xs font-black uppercase tracking-wider text-slate-500">
              What&apos;s your name?
            </label>
            <Input
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              placeholder="e.g. Alex"
              className="rounded-2xl border-2 border-slate-300 py-6 text-lg font-bold"
              data-testid="player-name-input"
              onKeyDown={(e) => e.key === "Enter" && tempName.trim() && setOnboardStep("avatar")}
            />
            <Button
              onClick={() => {
                if (!tempName.trim()) return;
                Sound.primeOnFirstInteraction();
                Sound.click();
                setOnboardStep("avatar");
              }}
              disabled={!tempName.trim()}
              data-testid="start-game-btn"
              className="w-full bg-amber-400 hover:bg-amber-500 text-slate-900 font-black font-heading text-lg py-7 rounded-2xl btn-3d border-2 border-amber-500 disabled:opacity-50"
            >
              Next: pick my look →
            </Button>
            <button
              onClick={() => { Sound.click(); setSaveOpen(true); }}
              data-testid="onboarding-load-btn"
              className="w-full text-xs font-black uppercase tracking-widest text-sky-600 hover:text-sky-800 pt-1"
            >
              Have a save code? Load my game
            </button>
          </div>
          <SaveLoadModal open={saveOpen} onClose={() => setSaveOpen(false)} />
        </div>
      </div>
    );
  }

  const earnedAchievements = ACHIEVEMENTS.filter((a) => state.achievements.includes(a.id));

  return (
    <div className="min-h-screen dotted-bg" data-testid="hub-screen">
      <HUD showHome={false} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Hero */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end gap-5">
          <div className="flex items-end gap-2 shrink-0">
            <button
              onClick={() => { Sound.click(); setAvatarMakerOpen(true); }}
              data-testid="hub-avatar-btn"
              title="Edit my look"
              className="relative bg-white rounded-3xl border-4 border-amber-300 card-3d p-2 group"
            >
              <Avatar avatar={state.avatar} size={110} testid="hub-avatar" />
              <span className="absolute -bottom-2 -right-2 bg-amber-400 border-2 border-amber-600 rounded-full p-1.5 group-hover:animate-wiggle">
                <Palette className="w-4 h-4 text-slate-900" />
              </span>
            </button>
            <Mascot pet={state.avatar.pet} size={80} name={state.avatar.petName} testid="hub-mascot" />
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-amber-600 mb-2">
              Hi {state.playerName}!
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-none">
              Which business today?
            </h1>
            <p className="mt-3 text-base sm:text-lg text-slate-600 font-medium max-w-2xl">
              Tap a card to open your dashboard, take customer requests, and play the mini-game.
            </p>
          </div>
        </div>

        {/* Action row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <Button
            onClick={() => setPiggyOpen(true)}
            data-testid="open-piggy-btn"
            className="bg-blue-500 hover:bg-blue-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-blue-600 py-6"
          >
            <PiggyBank className="w-5 h-5 mr-1" /> Piggy Bank
          </Button>
          <Button
            onClick={() => setShopOpen(true)}
            data-testid="open-shop-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-orange-600 py-6"
          >
            <ShoppingBag className="w-5 h-5 mr-1" /> Shop
          </Button>
          <Button
            onClick={() => setAchievementsOpen((v) => !v)}
            data-testid="open-achievements-btn"
            className="bg-yellow-500 hover:bg-yellow-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-yellow-600 py-6"
          >
            <Trophy className="w-5 h-5 mr-1" /> Trophies
          </Button>
          <Button
            onClick={() => setDayEndOpen(true)}
            data-testid="end-day-open"
            className="bg-violet-500 hover:bg-violet-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-violet-600 py-6"
          >
            <Moon className="w-5 h-5 mr-1" /> End Day
          </Button>
          <Button
            onClick={() => { Sound.click(); setFlyerPickerOpen((v) => !v); }}
            data-testid="open-flyers-btn"
            className="bg-rose-500 hover:bg-rose-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-rose-600 py-6 col-span-2"
          >
            <Megaphone className="w-5 h-5 mr-1" /> Flyers &amp; Marketing
          </Button>
          <Button
            onClick={() => { Sound.click(); setSaveOpen(true); }}
            data-testid="save-load-open"
            className="bg-sky-500 hover:bg-sky-600 text-white font-heading font-black rounded-2xl btn-3d border-2 border-sky-600 py-6 col-span-2"
          >
            <Save className="w-5 h-5 mr-1" /> Save &amp; Load
          </Button>
        </div>

        {flyerPickerOpen && (
          <div className="mb-8 bg-rose-50 border-2 border-rose-300 rounded-3xl p-5 animate-pop-in" data-testid="flyer-picker">
            <h3 className="font-display text-xl font-bold text-slate-900 mb-1">Which job do you want to market?</h3>
            <p className="text-sm text-slate-600 font-medium mb-3">Hand out flyers to get more customers tomorrow, or design a real flyer to print.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.values(BUSINESSES).map((b) => {
                const bl = isBusinessLocked(b.id);
                return (
                  <button
                    key={b.id}
                    onClick={() => { Sound.click(); if (bl) { setPaywallOpen(true); return; } navigate(`/business/${b.id}?tab=flyers`); }}
                    data-testid={`flyer-picker-${b.id}`}
                    className={`${b.lightBg} border-2 ${b.borderColor} rounded-2xl p-3 text-left card-3d ${bl ? "opacity-60" : ""}`}
                  >
                    <div className="text-3xl">{bl ? "🔒" : b.emoji}</div>
                    <div className="font-heading font-black text-sm text-slate-900">{b.name}</div>
                    <div className="text-[10px] font-bold text-slate-500">
                      {state.flyerLeads[b.id] ? `📬 ${state.flyerLeads[b.id]} calling tomorrow` : "📣 Hand out flyers"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Achievements panel */}
        {achievementsOpen && (
          <div className="mb-8 bg-yellow-50 border-2 border-yellow-300 rounded-3xl p-5 animate-pop-in" data-testid="achievements-panel">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-yellow-700" />
              <h3 className="font-display text-xl font-bold text-slate-900">
                Trophies · {earnedAchievements.length}/{ACHIEVEMENTS.length}
              </h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ACHIEVEMENTS.map((a) => {
                const earned = state.achievements.includes(a.id);
                return (
                  <div
                    key={a.id}
                    className={`rounded-2xl p-3 border-2 ${
                      earned ? "bg-white border-yellow-400" : "bg-slate-50 border-slate-200 opacity-50"
                    }`}
                  >
                    <div className="text-2xl mb-1">{a.icon}</div>
                    <div className="font-heading font-black text-sm text-slate-900">{a.title}</div>
                    <div className="text-xs text-slate-600">{a.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Business Cards */}
        {!state.unlocked && (
          <button
            onClick={() => { Sound.click(); setPaywallOpen(true); }}
            data-testid="unlock-banner"
            className="w-full mb-4 bg-slate-900 text-white rounded-2xl px-4 py-3 flex items-center justify-between gap-3 btn-3d border-2 border-slate-700 text-left"
          >
            <span className="font-heading font-black text-sm">🔒 Dog Walker is free · Unlock Babysitter, Craft Maker &amp; Lawn Mower</span>
            <span className="bg-amber-400 text-slate-900 font-black text-xs px-3 py-1 rounded-full shrink-0">$8.99 once</span>
          </button>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {Object.values(BUSINESSES).map((b) => {
            const tried = state.triedBusinesses.includes(b.id);
            const earn = state.earnings[b.id];
            const Icon = b.icon;
            const locked = isBusinessLocked(b.id);
            const brand = getBrand(b.id);
            const branded = !!state.brands[b.id];
            return (
              <button
                key={b.id}
                onClick={() => {
                  Sound.click();
                  if (locked) { setPaywallOpen(true); return; }
                  navigate(`/business/${b.id}`);
                }}
                data-testid={`business-card-${b.id}`}
                data-locked={locked ? "true" : "false"}
                className={`relative text-left ${b.lightBg} rounded-3xl p-5 border-4 ${b.borderColor} card-3d group ${locked ? "grayscale-[0.6]" : ""}`}
              >
                {locked && (
                  <div className="absolute -top-3 right-4 bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1" data-testid={`locked-badge-${b.id}`}>
                    <Lock className="w-3 h-3" /> Unlock
                  </div>
                )}
                <div className="flex items-start justify-between mb-3">
                  {branded ? (
                    <BrandBadge brand={brand} biz={b} size="md" showName={false} testid={`card-brand-${b.id}`} />
                  ) : (
                    <div className={`${b.solidBg} text-white rounded-2xl p-3 border-b-4 border-black/20`}>
                      <Icon className="w-7 h-7" strokeWidth={2.5} />
                    </div>
                  )}
                  <div className="text-5xl group-hover:animate-wiggle">{b.emoji}</div>
                </div>
                <div className={`text-[10px] font-black uppercase tracking-widest ${b.textColor} mb-1`}>
                  {branded ? b.name : tried ? "★ Tried" : "New"}
                </div>
                <h3 className="font-display text-2xl font-bold text-slate-900 break-words" data-testid={`card-name-${b.id}`}>
                  {branded ? brand.name : b.name}
                </h3>
                <p className="text-sm text-slate-600 font-medium mt-1">{b.tagline}</p>
                <div className="mt-3 text-xs text-slate-700 font-heading font-bold">
                  💵 Earn ${b.priceRange[0]}–${b.priceRange[1]} per job
                </div>
                {earn && earn.jobs > 0 && (
                  <div
                    className="mt-2 inline-flex items-center gap-1 bg-white/80 border-2 border-emerald-300 rounded-xl px-2 py-1 text-xs font-heading font-black text-emerald-800"
                    data-testid={`business-earnings-${b.id}`}
                  >
                    💰 ${earn.profit} profit · {earn.jobs} {earn.jobs === 1 ? "job" : "jobs"}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Rival + Leaderboard */}
        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          <RivalCard />
          <LeaderboardCard />
        </div>

        {/* Lessons list */}
        <div className="mt-10 bg-white rounded-3xl p-5 border-2 border-slate-200 card-3d">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h3 className="font-display text-xl font-bold text-slate-900">
              Business lessons
            </h3>
            <span className="text-xs font-black uppercase text-slate-500">
              {state.seenTips.length}/{LEARNING_TIPS.length}
            </span>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {LEARNING_TIPS.map((t) => {
              const seen = state.seenTips.includes(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => setTip(t)}
                  data-testid={`lesson-${t.id}`}
                  className={`text-left rounded-2xl p-3 border-2 transition-all ${
                    seen ? "bg-emerald-50 border-emerald-300" : "bg-slate-50 border-slate-200 hover:border-blue-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">{t.icon}</div>
                    <div>
                      <div className="font-heading font-black text-sm text-slate-900">{t.title}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        {t.topic} {seen && "· ✓ done"}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reset */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => {
              if (window.confirm("Start from $0? Your cash, piggy bank, trophies and days reset. Your name and avatar stay.")) {
                resetMoney();
                toast.success("Fresh start! You're at $0 — go earn it!");
              }
            }}
            data-testid="reset-money-btn"
            className="text-xs font-black uppercase tracking-widest text-slate-500 hover:text-amber-600 inline-flex items-center gap-1"
          >
            <Coins className="w-3 h-3" /> Start over from $0 (keep my avatar)
          </button>
          <button
            onClick={() => {
              if (window.confirm("Start over? All progress will be lost.")) resetGame();
            }}
            data-testid="reset-game-btn"
            className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-rose-500 inline-flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" /> Reset everything
          </button>
          <button
            onClick={async () => {
              const code = window.prompt("Admin code:");
              if (!code) return;
              try {
                const res = await adminUnlock(code.trim());
                if (!state.unlocked) {
                  Sound.fanfare();
                  setUnlocked("admin", res);
                  toast.success("Admin unlock: all businesses open!");
                }
                setAdminCode(code.trim());
              } catch (e) {
                Sound.error();
                toast.error(e?.response?.status === 429 ? "Too many tries — wait 15 minutes." : "Wrong code");
              }
            }}
            data-testid="admin-unlock-btn"
            className="text-xs font-black uppercase tracking-widest text-slate-300 hover:text-slate-500 inline-flex items-center gap-1"
          >
            <KeyRound className="w-3 h-3" /> Admin
          </button>
          <button
            onClick={() => { Sound.click(); navigate("/welcome"); }}
            data-testid="hub-about-btn"
            className="text-xs font-black uppercase tracking-widest text-slate-400 hover:text-amber-600 inline-flex items-center gap-1"
          >
            <Info className="w-3 h-3" /> About the game
          </button>
        </div>
      </main>

      <LearningTip tip={tip} open={!!tip} onClose={() => setTip(null)} />
      <PiggyBankModal open={piggyOpen} onClose={() => setPiggyOpen(false)} />
      <Shop open={shopOpen} onClose={() => setShopOpen(false)} />
      <EndOfDay open={dayEndOpen} onClose={() => setDayEndOpen(false)} />
      <WeekResultModal />
      <SaveLoadModal open={saveOpen} onClose={() => setSaveOpen(false)} />
      <AdminPanel open={!!adminCode} code={adminCode} onClose={() => setAdminCode(null)} />
      <div className="fixed bottom-4 right-4 z-30">
        <Button
          onClick={() => {
            const unseen = LEARNING_TIPS.filter((t) => !state.seenTips.includes(t.id));
            const pool = unseen.length > 0 ? unseen : LEARNING_TIPS;
            setTip(pool[Math.floor(Math.random() * pool.length)]);
          }}
          data-testid="random-tip-btn"
          className="bg-white hover:bg-amber-50 text-amber-600 font-black font-heading rounded-full btn-3d border-4 border-amber-300 w-14 h-14 p-0 shadow-lg"
        >
          <Sparkles className="w-6 h-6" />
        </Button>
      </div>
    </div>
  );
};

export default Hub;
